import { DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Typography, Button, Skeleton, Modal, message, Tooltip, Avatar } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton } from '@components/atoms/PrimaryButton';
import { useState } from 'react';
import { ProjectCard } from '@components/organisms/ProjectCard';
import { AddProjectModal, AddTestimonialModal } from '@components/molecules';
import { useAuth, useProjects, useTestimonials } from '@hooks';
import { Project } from '@common';
import './styles.css';

const { Title, Paragraph } = Typography;

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { projects, addProject, updateProject, archiveProject, getHighlightedProjects, toggleProjectHighlight } = useProjects();
  const { testimonials, addTestimonial, updateTestimonial, archiveTestimonial } = useTestimonials();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  
  // Testimonial modal states
  const [isAddTestimonialModalOpen, setIsAddTestimonialModalOpen] = useState(false);
  const [isEditTestimonialModalOpen, setIsEditTestimonialModalOpen] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<string | null>(null);
  const [selectedTestimonialId, setSelectedTestimonialId] = useState<string | null>(null);

  const isAuthenticated = !!profile.data;

  // Get highlighted projects for the featured section
  const highlightedProjectsQuery = getHighlightedProjects({ limit: 1 });

  // Get featured project for the featured section
  const getFeaturedProject = () => {
    // First try to get from highlighted projects API
    if (highlightedProjectsQuery.data?.data && highlightedProjectsQuery.data.data.length > 0) {
      return highlightedProjectsQuery.data.data[0];
    }
    
    // Fallback to checking isHighlight or featured field in main projects list
    const highlightedProjects = (projects.data || []).filter(project => 
      project.isHighlight || project.featured
    );
    if (highlightedProjects.length > 0) {
      return highlightedProjects[0];
    }
    
    // Return null instead of fallback to first project
    return null;
  };

  // Get active testimonials for display
  const getActiveTestimonials = () => {
    return (testimonials.data || []).filter(testimonial => testimonial.isActive).slice(0, 3);
  };

  const handleAddProject = (projectData: any) => {
    addProject.mutate(projectData, {
      onSuccess: () => {
        setIsAddModalOpen(false);
      },
      onError: (error) => {
        console.error('Error adding project:', error);
        message.error('Failed to add project. Please try again.');
      }
    });
  };

  const handleUpdateProject = (projectData: any) => {
    if (selectedProjectId) {
      updateProject.mutate(
        {
          id: selectedProjectId,
          data: projectData
        },
        {
          onSuccess: () => {
            setIsEditModalOpen(false);
            setSelectedProjectId(null);
          },
          onError: (error) => {
            console.error('Error updating project:', error);
            message.error('Failed to update project. Please try again.');
          }
        }
      );
    } else {
      message.error('No project selected for update');
    }
  };

  const confirmDelete = (id: string) => {
    setProjectToDelete(id);
  };

  const handleDelete = () => {
    if (projectToDelete) {
      archiveProject.mutate(projectToDelete, {
        onSuccess: () => {
          setProjectToDelete(null);
        }
      });
    }
  };

  const handleEditProject = (id: string) => {
    const project = projects.data?.find(proj => proj.id === id);

    if (project) {
      setSelectedProjectId(id);
      setIsEditModalOpen(true);
    } else {
      message.error('Error loading project data for editing');
    }
  };

  // Testimonial handlers
  const handleAddTestimonial = (testimonialData: any) => {
    addTestimonial.mutate(testimonialData, {
      onSuccess: () => {
        setIsAddTestimonialModalOpen(false);
      },
      onError: (error) => {
        console.error('Error adding testimonial:', error);
        message.error('Failed to add testimonial. Please try again.');
      }
    });
  };

  const handleUpdateTestimonial = (testimonialData: any) => {
    if (selectedTestimonialId) {
      updateTestimonial.mutate(
        {
          id: selectedTestimonialId,
          data: testimonialData
        },
        {
          onSuccess: () => {
            setIsEditTestimonialModalOpen(false);
            setSelectedTestimonialId(null);
          },
          onError: (error) => {
            console.error('Error updating testimonial:', error);
            message.error('Failed to update testimonial. Please try again.');
          }
        }
      );
    } else {
      message.error('No testimonial selected for update');
    }
  };

  const confirmDeleteTestimonial = (id: string) => {
    setTestimonialToDelete(id);
  };

  const handleDeleteTestimonial = () => {
    if (testimonialToDelete) {
      archiveTestimonial.mutate(testimonialToDelete, {
        onSuccess: () => {
          setTestimonialToDelete(null);
        }
      });
    }
  };

  const handleEditTestimonial = (id: string) => {
    const testimonial = testimonials.data?.find(test => test.id === id);

    if (testimonial) {
      setSelectedTestimonialId(id);
      setIsEditTestimonialModalOpen(true);
    } else {
      message.error('Error loading testimonial data for editing');
    }
  };

  const handleToggleProjectHighlight = (id: string) => {
    toggleProjectHighlight.mutate(id);
  };

  const viewProjectDetails = (id: string) => {
    navigate(`/projects/${id}`);
  };

  const featuredProject = getFeaturedProject();
  const activeTestimonials = getActiveTestimonials();

  // Helper function to get display name
  const getProjectDisplayName = (project: Project) => {
    return project.name || project.title || 'Untitled Project';
  };

  const getProjectImage = (project: Project) => {
    const imageUrl = import.meta.env.VITE_URL_FILES + '/' + project.image;
    return imageUrl || '/assets/images/project-default.jpg';
  };

  // Helper function to get project description
  const getProjectDescription = (project: Project) => {
    return project.description || 'No description available';
  };

  // Helper function to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).getFullYear().toString();
    } catch {
      return 'N/A';
    }
  };

  // Helper function to format budget with dollar sign
  const formatBudget = (budget?: string) => {
    if (!budget || budget === 'N/A') return 'N/A';
    
    // If budget already has dollar sign, return as is
    if (budget.includes('$')) return budget;
    
    // Try to parse as number and format with simple dollar sign
    const numericValue = parseFloat(budget.replace(/[^\d.-]/g, ''));
    if (!isNaN(numericValue)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(numericValue);
    }
    
    // Fallback: add $ prefix if not present
    return `$${budget}`;
  };

  // Helper function to render stars
  const renderStars = (stars: number) => {
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
  };
  
  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="projects-bg opacity-30 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
            OUR PROJECTS
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              Explore our portfolio of successful excavation and construction projects.
            </Paragraph>
          </div>
        </div>
      </section>     

      {/* Featured Project Detail */}
      {featuredProject && (
        <section className="w-full pt-8 pb-16 bg-second-black-carbon" id="featured-project">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div>
                <Title level={2} className="text-yellow-principal font-android !font-normal text-center mt-4">FEATURED PROJECT</Title>
                <Paragraph className="font-barlow text-center text-gray-300 pb-6">
                  Take a closer look at one of our recent major projects
                </Paragraph>
                <div className="bg-first-dark-gray relative">
                  {isAuthenticated && (
                    <div className="absolute top-4 right-4 z-10 flex gap-2">
                      <Tooltip title={featuredProject.isHighlight || featuredProject.featured ? "Remove highlight" : "Set as highlighted"}>
                        <Button
                          icon={featuredProject.isHighlight || featuredProject.featured ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          size="small"
                          onClick={() => handleToggleProjectHighlight(featuredProject.id)}
                          className="edit-button !rounded-none"
                        />
                      </Tooltip>
                      <Tooltip title="Edit project">
                        <Button
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => handleEditProject(featuredProject.id)}
                          className="edit-button !rounded-none"
                        />
                      </Tooltip>
                      <Tooltip title="Archive project">
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          onClick={() => confirmDelete(featuredProject.id)}
                          className="delete-button !rounded-none"
                        />
                      </Tooltip>
                    </div>
                  )}
                  
                  <div className="h-[500px] overflow-hidden mb-4">
                    <img 
                      src={getProjectImage(featuredProject)}
                      alt={getProjectDisplayName(featuredProject)}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = featuredProject.placeholderImage || 'https://placehold.co/1200x600?text=Project+Image';
                      }}
                    />
                  </div>              
                  <Title level={4} className="font-android !font-normal uppercase text-yellow-principal text-center md:text-justify mx-4">
                    {getProjectDisplayName(featuredProject)}
                  </Title>
                  <Paragraph className="font-barlow text-gray-300 text-justify text-lg mx-4">
                    {getProjectDescription(featuredProject)}
                  </Paragraph>
                  
                  {/* Big Numbers - Conditional Display */}
                  {(() => {
                    const bigNumbers = [];
                    
                    if (featuredProject.showDuration === true) {
                      bigNumbers.push(
                        <div key="duration" className="bg-zinc-800 text-center h-16 p-2">
                          <Paragraph className="text-gray-400 font-barlow !font-normal !text-sm !mb-1">Project Duration</Paragraph>
                          <Paragraph className="!text-white font-barlow font-semibold !text-sm">{featuredProject.duration || 'N/A'}</Paragraph>
                        </div>
                      );
                    }
                    
                    if (featuredProject.showYear === true) {
                      bigNumbers.push(
                        <div key="year" className="bg-zinc-800 text-center h-16 p-2">
                          <Paragraph className="text-gray-400 font-barlow !font-normal !text-sm !mb-1">Year</Paragraph>
                          <Paragraph className="!text-white font-barlow font-semibold !text-sm">
                            {featuredProject.completionYear || formatDate(featuredProject.endDate) || featuredProject.year || 'N/A'}
                          </Paragraph>
                        </div>
                      );
                    }
                    
                    if (featuredProject.showBudget === true) {
                      bigNumbers.push(
                        <div key="budget" className="bg-zinc-800 text-center h-16 p-2">
                          <Paragraph className="text-gray-400 font-barlow !font-normal !text-sm !mb-1">Budget</Paragraph>
                          <Paragraph className="!text-white font-barlow font-semibold !text-sm">
                            {formatBudget(featuredProject.budget)}
                          </Paragraph>
                        </div>
                      );
                    }
                    
                    if (featuredProject.showEarthMoved === true) {
                      bigNumbers.push(
                        <div key="earthMoved" className="bg-zinc-800 text-center h-16 p-2">
                          <Paragraph className="text-gray-400 font-barlow !font-normal !text-sm !mb-1">Earth Moved</Paragraph>
                          <Paragraph className="!text-white font-barlow font-semibold !text-sm">
                            {featuredProject.earthMoved || 'N/A'}
                          </Paragraph>
                        </div>
                      );
                    }
                    
                    // Se nenhum big number foi selecionado, não mostrar nada
                    if (bigNumbers.length === 0) {
                      return null;
                    }
                    
                    // Determinar o número de colunas baseado na quantidade de big numbers
                    const colClass = bigNumbers.length === 1 ? 'md:grid-cols-1' : 
                                   bigNumbers.length === 2 ? 'md:grid-cols-2' : 
                                   bigNumbers.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
                    
                    return (
                      <div className={`grid grid-cols-1 ${colClass} gap-6 mb-6 mx-4`}>
                        {bigNumbers}
                      </div>
                    );
                  })()}
                  <PrimaryButton 
                    type="primary" 
                    size="large"
                    className="m-4"
                    onClick={() => viewProjectDetails(featuredProject.id)}
                  >
                    VIEW PROJECT DETAILS
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Recent Projects */}
      <section className="w-full md:pt-8 pb-16 bg-second-black-carbon" id="recent-projects">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-yellow-principal font-android !font-normal text-center pb-4 md:pb-8">RECENT PROJECTS</Title>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div className="flex-1"></div>

            {isAuthenticated && (
              <PrimaryButton
                type="primary"
                icon={
                  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="#000000" aria-hidden="true">
                    <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z"></path>
                    <path d="M192 474h672q8 0 8 8v60q0 8-8 8H160q-8 0-8-8v-60q0-8 8-8z"></path>
                  </svg>
                }
                onClick={() => setIsAddModalOpen(true)}
                className="!bg-[#E3AF25] !text-black"
              >
                ADD PROJECT
              </PrimaryButton>
            )}
          </div>

          {projects.isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900 overflow-hidden p-6">
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ))}
            </div>
          )}

          {projects.isError && (
            <div className="text-center py-8">
              <Title level={4} className="!text-white !font-['Android_101']">
                ERROR LOADING PROJECTS
              </Title>
              <Button
                onClick={() => projects.refetch()}
                className="mt-4 !bg-[#E3AF25] !text-black"
              >
                TRY AGAIN
              </Button>
            </div>
          )}

          {!projects.isLoading && !projects.isError && (
            <>
              {/* Project Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(projects.data || []).length > 0 ? (
                  (projects.data || []).map(project => (
                    <div key={project.id} className="relative">
                      {isAuthenticated && (
                        <div className="absolute top-2 right-2 z-10 flex gap-1">
                          <Tooltip title={project.isHighlight || project.featured ? "Remove highlight" : "Set as highlighted"}>
                            <Button
                              icon={project.isHighlight || project.featured ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleProjectHighlight(project.id);
                              }}
                              className="edit-button !rounded-none"
                            />
                          </Tooltip>
                          <Tooltip title="Edit project">
                            <Button
                              icon={<EditOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditProject(project.id);
                              }}
                              className="edit-button !rounded-none"
                            />
                          </Tooltip>
                          <Tooltip title="Archive project">
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(project.id);
                              }}
                              className="delete-button !rounded-none"
                            />
                          </Tooltip>
                        </div>
                      )}
                      <ProjectCard project={project} navigate={navigate} />
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 text-center py-8">
                    <Title level={4} className="!text-white !font-['Android_101']">
                      NO PROJECTS FOUND
                    </Title>
                    <Paragraph className="text-gray-300 !font-['Barlow']">
                      We couldn't find any projects. Please check back later.
                    </Paragraph>
                  </div>
                )}
              </div>
            
            </>
          )}
        </div>
      </section>
      
      {/* Client Testimonials */}
      <section className="w-full pt-8 pb-16 bg-first-dark-gray">
        <div className="container px-4 mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <Title level={2} className="font-android !font-normal text-yellow-principal text-center md:text-left mt-4">CLIENT TESTIMONIALS</Title>
              <Paragraph className="font-barlow text-center md:text-left text-gray-300 text-lg pb-8">
                Hear what our clients have to say about working with Ruppert Inc.
              </Paragraph>
            </div>
            
            {isAuthenticated && activeTestimonials.length < 3 && (
              <PrimaryButton
                type="primary"
                icon={
                  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="#000000" aria-hidden="true">
                    <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z"></path>
                    <path d="M192 474h672q8 0 8 8v60q0 8-8 8H160q-8 0-8-8v-60q0-8 8-8z"></path>
                  </svg>
                }
                onClick={() => setIsAddTestimonialModalOpen(true)}
                className="!bg-[#E3AF25] !text-black"
              >
                ADD TESTIMONIAL
              </PrimaryButton>
            )}
          </div>

          {testimonials.isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900 p-8">
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ))}
            </div>
          )}

          {testimonials.isError && (
            <div className="text-center py-8">
              <Title level={4} className="!text-white !font-['Android_101']">
                ERROR LOADING TESTIMONIALS
              </Title>
              <Button
                onClick={() => testimonials.refetch()}
                className="mt-4 !bg-[#E3AF25] !text-black"
              >
                TRY AGAIN
              </Button>
            </div>
          )}

          {!testimonials.isLoading && !testimonials.isError && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {activeTestimonials.length > 0 ? (
                activeTestimonials.map((testimonial) => (
                  <div key={testimonial.id} className="bg-zinc-900 p-8 relative">
                    {isAuthenticated && (
                      <div className="absolute top-2 right-2 z-10 flex gap-1">
                        <Tooltip title="Edit testimonial">
                          <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTestimonial(testimonial.id);
                            }}
                            className="edit-button"
                          />
                        </Tooltip>
                        <Tooltip title="Archive testimonial">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteTestimonial(testimonial.id);
                            }}
                            className="delete-button"
                          />
                        </Tooltip>
                      </div>
                    )}
                    
                    <div className="flex mb-2">
                      <span className="text-yellow-principal text-xl">
                        {renderStars(testimonial.stars)}
                      </span>
                    </div>
                    <Paragraph className="font-barlow text-gray-300 italic mb-6">
                      "{testimonial.text}"
                    </Paragraph>
                    <div className="flex items-center">
                      <Avatar 
                        size={48} 
                        src={testimonial.avatar} 
                        className="!mr-4"
                        style={{ backgroundColor: '#E3AF25' }}
                      >
                        {testimonial.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <div>
                        <Title level={5} className="!text-white font-barlow !mb-0">{testimonial.name}</Title>
                        <Paragraph className="font-barlow text-gray-400 !text-sm !mb-0">
                          {testimonial.description || 'Client'}
                        </Paragraph>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-3 text-center py-8">
                  <Title level={4} className="!text-white !font-['Android_101']">
                    NO TESTIMONIALS FOUND
                  </Title>
                  <Paragraph className="text-gray-300 !font-['Barlow']">
                    No active testimonials available at the moment.
                  </Paragraph>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Add Project Modal */}
      <AddProjectModal
        isOpen={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onSave={handleAddProject}
      />

      {/* Edit Project Modal */}
      {isEditModalOpen && selectedProjectId && (
        <AddProjectModal
          isOpen={isEditModalOpen}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedProjectId(null);
          }}
          onSave={(projectData) => handleUpdateProject(projectData)}
          initialValues={projects.data?.find(proj => proj.id === selectedProjectId)}
        />
      )}

      {/* Add Testimonial Modal */}
      <AddTestimonialModal
        isOpen={isAddTestimonialModalOpen}
        onCancel={() => setIsAddTestimonialModalOpen(false)}
        onSave={handleAddTestimonial}
      />

      {/* Edit Testimonial Modal */}
      {isEditTestimonialModalOpen && selectedTestimonialId && (
        <AddTestimonialModal
          isOpen={isEditTestimonialModalOpen}
          onCancel={() => {
            setIsEditTestimonialModalOpen(false);
            setSelectedTestimonialId(null);
          }}
          onSave={(testimonialData) => handleUpdateTestimonial(testimonialData)}
          initialValues={testimonials.data?.find(test => test.id === selectedTestimonialId)}
        />
      )}

      {/* Delete Project Confirmation Modal */}
      <Modal
        title={<span className="font-android !font-normal">CONFIRM ARCHIVE</span>}
        open={!!projectToDelete}
        onCancel={() => setProjectToDelete(null)}
        className="delete-confirm-modal"
        footer={[
          <Button
            key="cancel"
            onClick={() => setProjectToDelete(null)}
            className="font-barlow text-white mr-2"
          >
            <span className="text-white">CANCEL</span>
          </Button>,
          <PrimaryButton
            key="archive"
            danger
            onClick={handleDelete}
            className="font-barlow text-black"
          >
            ARCHIVE
          </PrimaryButton>
        ]}
      >
        <p className="font-barlow">Are you sure you want to archive this project? This action can be undone later.</p>
      </Modal>

      {/* Delete Testimonial Confirmation Modal */}
      <Modal
        title={<span className="font-android !font-normal">CONFIRM ARCHIVE</span>}
        open={!!testimonialToDelete}
        onCancel={() => setTestimonialToDelete(null)}
        className="delete-confirm-modal"
        footer={[
          <Button
            key="cancel"
            onClick={() => setTestimonialToDelete(null)}
            className="font-barlow text-white mr-2"
          >
            <span className="text-white">CANCEL</span>
          </Button>,
          <PrimaryButton
            key="archive"
            danger
            onClick={handleDeleteTestimonial}
            className="font-barlow text-black"
          >
            ARCHIVE
          </PrimaryButton>
        ]}
      >
        <p className="font-barlow">Are you sure you want to archive this testimonial? This action can be undone later.</p>
      </Modal>
    </div>
  );
}; 